import React, { useState, useEffect, useMemo } from "react";
import Container from "@/components/marketing/Container";
import { Link } from "react-router-dom";
import { base44 } from "@/api/base44Client";
import SectionHeading from "@/components/marketing/SectionHeading";
import NewswireCard from "./NewswireCard";
import Reveal from "@/components/marketing/Reveal";

const capitalize = (s) => (s ? s.charAt(0).toUpperCase() + s.slice(1) : "");

const REGION_TABS = [
  "All",
  "Africa",
  "Asia Pacific",
  "Europe",
  "Middle East",
  "Latin America",
  "North America",
  "Global",
];

/**
 * Consolidate the raw newswire_region into one of the seven filter regions.
 * Gulf States, MENAP, and Middle East merge into "Middle East".
 * Southeast Asia, ASEAN, Greater China, Greater Bay Area merge into "Asia Pacific".
 * Africa variants map to "Africa". Wires with no region fall back to
 * name/country signals.
 */
function deriveRegion(pkg) {
  const raw = pkg.newswire_region;
  if (raw === "Gulf States" || raw === "MENAP" || raw === "Middle East") return "Middle East";
  if (raw && raw.startsWith("Africa")) return "Africa";
  if (raw && ["Asia Pacific", "Southeast Asia", "ASEAN", "Greater China", "Greater Bay Area"].includes(raw)) return "Asia Pacific";
  if (raw === "Europe" || raw === "UK") return "Europe";
  if (raw === "Latin America") return "Latin America";
  if (raw === "North America") return "North America";
  if (raw === "Global") return "Global";
  const name = (pkg.name || "").toLowerCase();
  const countries = pkg.countries || [];
  if (name.includes("global") || name.includes("globe") || name.includes("premium") || name.includes("standard")) return "Global";
  if (countries.length >= 3) return "Global";
  if (countries.some((c) => ["USA", "United States", "Canada"].includes(c))) return "North America";
  if (countries.some((c) => ["UK", "United Kingdom"].includes(c))) return "Europe";
  return "Global";
}

/**
 * Derive sector tags. Only Crypto and Finance are genuine sector-specific
 * wires; everything else returns an empty array (no pseudo-sector tag).
 */
function deriveSectors(pkg) {
  const name = (pkg.name || "").toLowerCase();
  if (name.includes("crypto")) return ["crypto"];
  if (name.includes("finance")) return ["finance"];
  return [];
}

/**
 * Filterable grid of newswire packages, bound to the Package entity
 * (category: "newswires"). Two filter dimensions: Region and Sector.
 */
export default function NewswireCatalogue() {
  const [newswires, setNewswires] = useState([]);
  const [loading, setLoading] = useState(true);
  const [regionFilter, setRegionFilter] = useState("All");
  const [sectorFilter, setSectorFilter] = useState("All");

  useEffect(() => {
    let active = true;
    base44.entities.Package.filter(
      { category: "newswires", is_active: true, retail_visible: { $ne: false } },
      "-created_date",
      100
    )
      .then((data) => {
        if (!active) return;
        const enriched = (data || []).map((p) => ({
          ...p,
          derivedRegion: deriveRegion(p),
          derivedSectors: deriveSectors(p),
        }));
        enriched.sort((a, b) => (b.media_outlets_count || 0) - (a.media_outlets_count || 0));
        setNewswires(enriched);
        setLoading(false);
      })
      .catch(() => {
        if (!active) return;
        setLoading(false);
      });
    return () => {
      active = false;
    };
  }, []);

  const regions = REGION_TABS;

  const sectors = useMemo(() => {
    const set = new Set(newswires.flatMap((n) => n.derivedSectors));
    return ["All", ...Array.from(set).sort()];
  }, [newswires]);

  const filtered = newswires.filter((n) => {
    const regionMatch = regionFilter === "All" || n.derivedRegion === regionFilter;
    const sectorMatch = sectorFilter === "All" || n.derivedSectors.includes(sectorFilter);
    return regionMatch && sectorMatch;
  });

  const pillBase = "px-4 py-2 rounded-full text-sm font-medium transition-all duration-200";
  const pillActive = "bg-cobalt text-white shadow-sm";
  const pillInactive = "bg-white border-[0.5px] border-[#e8e6ef] text-[#3a3646] hover:border-[#7200CB]";

  return (
    <section className="py-7 sm:py-9 lg:py-12 bg-[#f7f7f9]">
      <Container>
        <Reveal>
          <SectionHeading
            tag="CATALOGUE"
            title="Browse the newswire networks"
            subtitle="Filter by region or sector. Each newswire shows its guaranteed key site pickups, total reach, and price. Combine multiple wires for broader coverage."
          />
        </Reveal>

        {/* Region filter */}
        <div className="flex flex-wrap items-center justify-center gap-2 mb-4">
          {regions.map((r) => (
            <button
              key={r}
              type="button"
              onClick={() => setRegionFilter(r)}
              className={`${pillBase} ${regionFilter === r ? pillActive : pillInactive}`}
            >
              {r === "All" ? "All regions" : r}
            </button>
          ))}
        </div>

        {/* Sector filter */}
        <div className="flex flex-wrap items-center justify-center gap-2 mb-10">
          {sectors.map((s) => (
            <button
              key={s}
              type="button"
              onClick={() => setSectorFilter(s)}
              className={`${pillBase} ${sectorFilter === s ? pillActive : pillInactive}`}
            >
              {s === "All" ? "All sectors" : capitalize(s)}
            </button>
          ))}
        </div>

        {/* Grid */}
        {loading ? (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 lg:gap-6">
            {Array.from({ length: 6 }).map((_, i) => (
              <div
                key={i}
                className="bg-white border border-[#e8e6ef] rounded-xl h-64 animate-pulse"
              />
            ))}
          </div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-16">
            <p className="text-[#6b6880] text-base mb-4">
              No newswires match this filter combination.
            </p>
            <Link to="/contact" className="text-cobalt font-medium hover:underline">
              Contact us about custom distribution options
            </Link>
          </div>
        ) : (
          <Reveal variant="card" delay={0.08}>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 lg:gap-6">
              {filtered.map((ns) => (
                <NewswireCard key={ns.id || ns.name} newswire={ns} />
              ))}
            </div>
          </Reveal>
        )}

        <p className="text-center text-[#6b6880] text-sm mt-10 max-w-2xl mx-auto">
          Prices shown in USD. Each newswire includes guaranteed key site pickups with a full pickup report. Combine multiple newswires for broader regional and sector coverage.
        </p>
      </Container>
    </section>
  );
}